/**
 * Chat-owned image generation jobs.
 *
 * The runner lives in a module singleton, not in ChatPage, so navigating to
 * another page does not abort the request. Pending/completed state is written
 * to IndexedDB, and the chat page's live query renders the same assistant
 * bubble when the user returns.
 */
import type { GeneratedImage, ResolvedImageConfig } from '../../core/image';
import { getDb } from '../../core/db';
import { conversationRepo, messageRepo } from '../../core/repositories';
import type { ChatMessage } from '../../core/types';
import { compressGeneratedImages } from '../gallery/compress';
import { saveGeneratedImages } from '../gallery/save';
import type { SaveGeneratedImagesOptions, SaveGeneratedImagesResult } from '../gallery/save';
import { defaultImageGenerator } from './imageEntry';
import type { ImageGenerator } from './imageEntry';
import type { ServerImageRequest } from './serverImage';
import { modelGateway } from '../modelGateway';
import { getMomentAccountId } from '../moments/accountScope';

export interface ImageJobInput {
  messageId: string;
  prompt: string;
  providerId: string;
  model: string;
  config: ResolvedImageConfig | null;
  serverModel: string | null;
  generateImage?: ImageGenerator;
  saveImages?: (
    images: readonly GeneratedImage[],
    options: SaveGeneratedImagesOptions,
  ) => Promise<SaveGeneratedImagesResult>;
  generateServer?: (request: ServerImageRequest) => Promise<GeneratedImage[]>;
}

export const IMAGE_JOB_FINISHED_EVENT = 'zxtalk:image-job-finished';

type ImageSave = NonNullable<ImageJobInput['saveImages']>;

const activeByDb = new WeakMap<ReturnType<typeof getDb>, Map<string, AbortController>>();
const roleDeliveryByDb = new WeakMap<ReturnType<typeof getDb>, Set<string>>();

export function registerRoleImageDelivery(messageId: string): () => void {
  const db = getDb();
  let deliveries = roleDeliveryByDb.get(db);
  if (!deliveries) { deliveries = new Set(); roleDeliveryByDb.set(db, deliveries); }
  deliveries.add(messageId);
  return () => { deliveries.delete(messageId); };
}

function activeJobs(): Map<string, AbortController> {
  const db = getDb();
  let active = activeByDb.get(db);
  if (active === undefined) {
    active = new Map();
    activeByDb.set(db, active);
  }
  return active;
}

export function activeImageJobController(messageId: string): AbortController | undefined {
  return activeJobs().get(messageId);
}

export function isImageJobActive(messageId: string): boolean {
  return activeJobs().has(messageId);
}

async function finishConversation(message: ChatMessage): Promise<void> {
  const conversation = await conversationRepo.getConversation(message.conversationId);
  if (conversation === undefined) return;
  await conversationRepo.updateConversation(conversation.id, {
    messageCount: await messageRepo.countMessages(conversation.id),
  });
}

async function runImageJob(input: ImageJobInput, controller: AbortController): Promise<void> {
  const userId = getMomentAccountId();
  try {
    const message = await messageRepo.getMessage(input.messageId);
    const conversation = message ? await conversationRepo.getConversation(message.conversationId) : undefined;
    await messageRepo.updateMessage(input.messageId, {
      status: 'streaming',
      content: `正在生成图片…${input.model.trim() === '' ? '' : `（${input.model}）`}`,
    });

    let images: GeneratedImage[];
    if (input.serverModel !== null) {
      images = await (input.generateServer ?? ((request) => modelGateway.generateImage({
        prompt: request.prompt,
        serverModel: request.model,
        ...(request.signal ? { signal: request.signal } : {}),
      })))({
        prompt: input.prompt,
        model: input.serverModel,
        signal: controller.signal,
      });
    } else {
      if (input.config === null) throw new Error('生图配置不可用');
      images = await (input.generateImage ?? defaultImageGenerator)({
        prompt: input.prompt,
        signal: controller.signal,
        config: input.config,
      });
    }
    if (images.length === 0) throw new Error('生图服务没有返回图片');

    images = await compressGeneratedImages(images);
    const galleryModel = input.serverModel ?? input.model;
    const save: ImageSave = input.saveImages ?? saveGeneratedImages;
    let savedCount = 0;
    let saveWarning: string | undefined;
    try {
      const result = await save(images, { prompt: input.prompt, model: galleryModel,
        ...(conversation?.presetId === undefined ? {} : { presetId: conversation.presetId }),
        ...(message === undefined ? {} : { conversationId: message.conversationId }),
        ...(userId === null ? {} : { userId }),
      });
      savedCount = result.saved.length;
      saveWarning = result.warning;
    } catch {
      // A gallery write failure must not discard an image the user already paid for.
      saveWarning = '图库写入失败，本次未存入图库';
    }

    const saved = await messageRepo.updateMessage(input.messageId, {
      content: `已生成图片：${input.prompt}`,
      status: 'done',
      images: images.map((image) => image.dataUrl),
      imageJob: undefined,
    });
    if (saved !== undefined) {
      await finishConversation(saved);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(IMAGE_JOB_FINISHED_EVENT, {
            detail: {
              conversationId: saved.conversationId,
              saved: savedCount,
              ...(saveWarning === undefined ? {} : { warning: saveWarning }),
            },
          }),
        );
      }
    }
  } catch (caught) {
    if (!isImageJobActive(input.messageId)) return;
    const aborted = controller.signal.aborted;
    const reason =
      aborted
        ? '已停止生成图片'
        : caught instanceof Error
          ? `图片生成失败：${caught.message}`
          : '图片生成失败，请稍后重试';
    const saved = await messageRepo.updateMessage(input.messageId, {
      content: reason,
      status: 'error',
    });
    if (saved !== undefined) await finishConversation(saved);
  } finally {
    const active = activeJobs();
    if (active.get(input.messageId) === controller) active.delete(input.messageId);
  }
}

/** Start one background image job for an already-persisted assistant message. */
export function startImageJob(input: ImageJobInput): void {
  if (activeJobs().has(input.messageId)) return;
  const controller = new AbortController();
  activeJobs().set(input.messageId, controller);
  void runImageJob(input, controller);
}

export function abortImageJob(messageId: string): void {
  activeJobs().get(messageId)?.abort();
}

/**
 * On a fresh app mount, in-flight client jobs from a previous page lifecycle
 * cannot be recovered safely (the upstream request may already have been
 * charged). Mark them as interrupted so they never look permanently busy.
 */
export async function interruptStaleImageJobs(): Promise<void> {
  const active = activeJobs();
  const messages = await getDb().messages.toArray();
  const activeDeliveries = roleDeliveryByDb.get(getDb());
  await Promise.all(messages.filter(message => message.imageSending && !activeDeliveries?.has(message.id))
    .map(message => messageRepo.updateMessage(message.id, { imageSending: false })));
  await Promise.allSettled(
    messages
      .filter(
        (message) =>
          message.imageJob !== undefined &&
          ['pending', 'streaming'].includes(message.status) &&
          !active.has(message.id),
      )
      .map(async (message) => {
        const saved = await messageRepo.updateMessage(message.id, {
          content: '图片生成已中断，请重新发送提示词',
          status: 'error',
        });
        if (saved !== undefined) await finishConversation(saved);
      }),
  );
}
